import { Epic, combineEpics, ofType } from 'redux-observable';
import { createTryActionsEpic, createTryActionFromStoreEpic } from './Common';
import {
    StoreState,
    InstructorExperience,
    InstructorCertification,
    InstructorKudos,
    InstructorJobOffer,
    InstructorInvitation,
} from '../types';
import * as api from '../api';
import {
    GetInstructorProfile,
    AddInstructorExperience,
    AddInstructorCertifications,
    GiveInstructorKudos,
    OfferInstructorJob,
    InviteInstructor
} from '../api/Commands';
import _ from 'lodash';
import {
    InstructorAction,
    GET_INSTRUCTOR_PROFILE,
    instructorUpdated,
    InstructorProfileFields,
    UPDATE_INSTRUCTOR_PROFILE,
    ADD_INSTRUCTOR_EXPERIENCE,
    ADD_INSTRUCTOR_CERTIFICATIONS,
    GIVE_INSTRUCTOR_KUDOS,
    instructorKudosUpdated,
    OFFER_INSTRUCTOR_JOB,
    INVITE_INSTRUCTOR,
    ACTIVATE_INSTRUCTOR_ACCOUNT,
    instructorSessionUpdated
} from '../actions/InstructorActions';
import { Action } from 'redux';
import { UserSignedIn, USER_SIGNED_IN } from '../actions/AuthActions';
import {
    observeChangesDoc,
    firebaseStore,
    firebaseStorage,
    firebaseAuth,
    observeChanges
} from '../firebase';
import { switchMap, map, catchError } from 'rxjs/operators';

const instructorDoc = (uid: string) => firebaseStore.collection('instructors').doc(uid);

const getInstructorProfile = async ({ accountName }: { accountName: string }, { instructors }: StoreState) => {
    const command: GetInstructorProfile = {
        accountName: accountName
    };

    return await api.getInstructorProfile(command);
};

const addInstructorExperience = async (experience: InstructorExperience[], { auth }: StoreState) => {
    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to add instructor experience when not signed in`);
    }

    const command: AddInstructorExperience = {
        experience
    };

    return await api.addInstructorExperience(command);
};

const addInstructorCertifications = async (certifications: InstructorCertification[], { auth }: StoreState) => {
    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to add instructor experience when not signed in`);
    }

    const command: AddInstructorCertifications = {
        certifications: certifications
    };

    return await api.addInstructorCertifications(command);
};

const giveInstructorKudos = async (kudos: InstructorKudos, { auth }: StoreState) => {
    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to give instructor kudos when not signed in`);
    }

    if (!kudos.instructorId) {
        throw new Error(`invalid instructorId`);
    }

    const command: GiveInstructorKudos = {
        kudos: kudos
    };

    await api.giveInstructorKudos(command);
};

const offerInstructorJob = async (jobOffer: InstructorJobOffer, { auth }: StoreState) => {
    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to offer instructor job when not signed in`);
    }

    if (!jobOffer.instructorId) {
        throw new Error(`invalid instructorId`);
    }

    const command: OfferInstructorJob = {
        jobOffer: jobOffer
    };

    await api.offerInstructorJob(command);
};

const inviteInstructor = async (invitation: InstructorInvitation, { auth }: StoreState) => {
    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to invite instructor when not signed in`);
    }

    if (!invitation.instructorId) {
        throw new Error(`invalid instructorId`);
    }

    const command: InviteInstructor = {
        invitation: invitation
    };

    await api.inviteInstructor(command);
};

const activateInstructorAccount = async ({ email, password, href }: { email: string; password: string; href: string }) => {
    try {
        await firebaseAuth.signInWithEmailLink(email, href);
    } catch (err) {
        // TODO send a new instructor email?
        console.error(err);
        throw new Error('Activation failed, try reset password from sign-in page.');
    }

    if (firebaseAuth.currentUser) {
        await firebaseAuth.currentUser.updatePassword(password);
    }
};

const updateInstructorProfile = async (fields: InstructorProfileFields, { instructors, auth }: StoreState) => {
    const {
        name,
        bio,
        activities,
        languages,
        photoPreviewFile,
        backgroundPreviewFile,
        kudos,
        status,
        gallery,
        social
    } = fields;

    if (!auth.user || !firebaseAuth.currentUser) {
        throw new Error(`attempted to update instructor when not signed in`);
    }

    if (!instructors.instructor) {
        throw new Error(`instructor document doesn't exist`);
    }

    const profile = instructors.instructor.profile;
    let newInstructor = { ...instructors.instructor };

    if (name) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                name: name
            }
        };
        await firebaseAuth.currentUser.updateProfile({ displayName: name });
    }

    if (bio) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                bio: bio
            }
        };
    }

    if (activities) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                activities: activities
            }
        };
    }

    if (languages) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                languages: languages
            }
        };
    }

    if (social) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                social: social
            }
        };
    }

    if (photoPreviewFile && photoPreviewFile.name) {
        const ext = _.last(photoPreviewFile.name.split('.'));
        const ref = firebaseStorage.ref().child(`instructors/${auth.user.uid}/images/profile.${ext}`);
        const snapshot = await ref.put(photoPreviewFile);
        const photoURL = await snapshot.ref.getDownloadURL();

        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                photoURL: photoURL
            }
        };
        await firebaseAuth.currentUser.updateProfile({ photoURL: photoURL });
    }

    if (backgroundPreviewFile && backgroundPreviewFile.name) {
        const ext = _.last(backgroundPreviewFile.name.split('.'));
        const ref = firebaseStorage.ref().child(`instructors/${auth.user.uid}/images/background.${ext}`);
        const snapshot = await ref.put(backgroundPreviewFile);
        const backgroundURL = await snapshot.ref.getDownloadURL();

        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                backgroundURL: backgroundURL
            }
        };
    }

    if (kudos) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                kudos: kudos
            }
        };
    }

    if (status) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                status: status
            }
        };
    }

    if (gallery) {
        newInstructor = {
            ...newInstructor,
            profile: {
                ...profile,
                gallery: gallery
            }
        };
    }

    await instructorDoc(auth.user.uid).set(newInstructor, { merge: true });
};

const instructorUpdatedEpic: Epic<Action, Action, StoreState> =
    (action$) =>
        action$
            .pipe(
                ofType<Action, UserSignedIn>(USER_SIGNED_IN),
                switchMap(user => observeChangesDoc(`instructors/${user.value.user.uid}`)),
                map(instructorUpdated),
                catchError((err, caught) => {
                    console.log('instructorUpdatedEpic', err);
                    return caught;
                })
            );

const instructorSessionUpdatedEpic: Epic<Action, Action, StoreState> =
    (action$) =>
        action$
            .pipe(
                ofType<Action, UserSignedIn>(USER_SIGNED_IN),
                switchMap(user => observeChanges(`instructors/${user.value.user.uid}/sessions`)),
                map(instructorSessionUpdated),
                catchError((err, caught) => {
                    console.log('instructorSessionUpdatedEpic', err);
                    return caught;
                })
            );

const instructorKudosUpdatedEpic: Epic<Action, Action, StoreState> =
    (action$) =>
        action$
            .pipe(
                ofType<Action, UserSignedIn>(USER_SIGNED_IN),
                switchMap(user => observeChanges(`instructors/${user.value.user.uid}/kudos`)),
                map(instructorKudosUpdated),
                catchError((err, caught) => {
                    console.log('instructorKudosUpdatedEpic', err);
                    return caught;
                })
            );

const updateInstructorProfileEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        UPDATE_INSTRUCTOR_PROFILE,
        (a, s) => updateInstructorProfile(a.value, s),
        (a, r) => r);

const addInstructorSchoolsEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        ADD_INSTRUCTOR_EXPERIENCE,
        (a, s) => addInstructorExperience(a.value, s),
        (a, r) => r);

const addInstructorCertificationsEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        ADD_INSTRUCTOR_CERTIFICATIONS,
        (a, s) => addInstructorCertifications(a.value, s),
        (a, r) => r);

const giveInstructorKudosEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        GIVE_INSTRUCTOR_KUDOS,
        (a, s) => giveInstructorKudos(a.value, s),
        (a, r) => r);

const offerInstructorJobEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        OFFER_INSTRUCTOR_JOB,
        (a, s) => offerInstructorJob(a.value, s),
        (a, r) => r);

const inviteInstructorEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        INVITE_INSTRUCTOR,
        (a, s) => inviteInstructor(a.value, s),
        (a, r) => r);

const activateInstructorAccountEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        ACTIVATE_INSTRUCTOR_ACCOUNT,
        (a, s) => activateInstructorAccount(a.value),
        (a, r) => r);

const getInstructorEpic: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionsEpic(
        GET_INSTRUCTOR_PROFILE,
        (a, s) => getInstructorProfile(a.value, s),
        (a, r) => r);

const getInstructorEpicFromStore: Epic<InstructorAction, InstructorAction, StoreState> =
    createTryActionFromStoreEpic(
        s => s.instructors.getInstructorProfileAction as InstructorAction,
        (a, s) => getInstructorProfile(a.value, s),
        (a, r) => r);

export default combineEpics(
    getInstructorEpic,
    getInstructorEpicFromStore,
    instructorUpdatedEpic,
    instructorSessionUpdatedEpic,
    instructorKudosUpdatedEpic,
    updateInstructorProfileEpic,
    addInstructorSchoolsEpic,
    addInstructorCertificationsEpic,
    giveInstructorKudosEpic,
    offerInstructorJobEpic,
    inviteInstructorEpic,
    activateInstructorAccountEpic,
);
